import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../utils/apiClient";
import { initialFilters, initialOptions } from "../constants";

export function useQbrFilters() {
  const [filters, setFilters] = useState(initialFilters);
  const [options, setOptions] = useState(initialOptions);
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchFilterOptions = async () => {
      setIsFiltersLoading(true);
      setError("");

      try {
        const response = await apiClient.get("/api/v1/difotVisualisation/filter-options", {
          signal: controller.signal,
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.detail || payload.error || "Failed to load DIFOT visualisation filters");
        }

        const payload = await response.json();
        if (cancelled) return;

        setOptions({
          financialYears: Array.isArray(payload.financialYears) ? payload.financialYears : [],
          financialQuarters:
            payload.financialQuarters && typeof payload.financialQuarters === "object" && !Array.isArray(payload.financialQuarters)
              ? payload.financialQuarters
              : {},
          parentCompanies: Array.isArray(payload.parentCompanies) ? payload.parentCompanies : [],
          childCompanies:
            payload.childCompanies && typeof payload.childCompanies === "object" && !Array.isArray(payload.childCompanies)
              ? payload.childCompanies
              : {},
          carriers: Array.isArray(payload.carriers) ? payload.carriers : [],
          services:
            payload.services && typeof payload.services === "object" && !Array.isArray(payload.services)
              ? payload.services
              : {},
        });
      } catch (err) {
        if (err.name === "AbortError" || cancelled) return;
        setOptions(initialOptions);
        setError(err.message || "Failed to load DIFOT visualisation filters");
      } finally {
        if (!cancelled) setIsFiltersLoading(false);
      }
    };

    fetchFilterOptions();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const quarterOptions = useMemo(
    () => (filters.financialYear ? options.financialQuarters[filters.financialYear] || [] : []),
    [filters.financialYear, options.financialQuarters]
  );

  /* Child companies can be picked on their own. With parents selected the list
     narrows to their children; with none it holds every child company. */
  const childCompanyOptions = useMemo(() => {
    const parents = filters.parentCompany.length
      ? filters.parentCompany
      : Object.keys(options.childCompanies);
    return Array.from(new Set(parents.flatMap((parent) => options.childCompanies[parent] || []))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [filters.parentCompany, options.childCompanies]);

  const serviceOptions = useMemo(() => {
    if (!filters.carrier.length) return [];
    return Array.from(new Set(filters.carrier.flatMap((carrier) => options.services[carrier] || []))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [filters.carrier, options.services]);

  const canLoadData = Boolean(filters.financialYear && filters.financialQuarter);

  const handleSingleChange = (key) => (option) => {
    const nextValue = option?.value || "";
    setFilters((current) => ({ ...current, [key]: nextValue }));
  };

  const handleMultiChange = (key) => (selectedOptions) => {
    const values = Array.isArray(selectedOptions) ? selectedOptions.map((item) => item.value) : [];
    setFilters((current) => ({ ...current, [key]: values }));
  };

  const handleYearChange = (option) => {
    const nextYear = option?.value || "";
    setFilters((current) => ({
      ...current,
      financialYear: nextYear,
      financialQuarter: "",
    }));
  };

  /* Keep the child companies that still fit: all of them when the parent filter
     is cleared, otherwise only those belonging to a selected parent. */
  const handleParentCompanyChange = (selectedOptions) => {
    const values = Array.isArray(selectedOptions) ? selectedOptions.map((item) => item.value) : [];
    setFilters((current) => {
      const allowed = new Set(values.flatMap((parent) => options.childCompanies[parent] || []));
      return {
        ...current,
        parentCompany: values,
        childCompany: values.length
          ? current.childCompany.filter((child) => allowed.has(child))
          : current.childCompany,
      };
    });
  };

  const handleCarrierChange = (selectedOptions) => {
    const values = Array.isArray(selectedOptions) ? selectedOptions.map((item) => item.value) : [];
    setFilters((current) => ({
      ...current,
      carrier: values,
      service: [],
    }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  return {
    filters,
    options,
    isFiltersLoading,
    error,
    quarterOptions,
    childCompanyOptions,
    serviceOptions,
    canLoadData,
    handleSingleChange,
    handleMultiChange,
    handleYearChange,
    handleParentCompanyChange,
    handleCarrierChange,
    resetFilters,
  };
}
